/**
 * Dynamic GA4 (Google tag / gtag.js) loader.
 *
 * Nothing in this module executes at import time — `loadGA4()` must be
 * called explicitly (only done by ConsentContext once the visitor grants
 * analytics consent). This keeps GA4 from making any network request before
 * consent is recorded.
 */

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-X6D8RQMV1C';

let scriptEl = null;
let loaded = false;

/** Whether the GA4 tag is currently injected. */
export const isGA4Loaded = () => loaded;

/**
 * Injects the GA4 gtag.js tag and configures it. Idempotent — safe to call
 * repeatedly (e.g. on every consent-state change) without duplicating tags.
 */
export function loadGA4() {
  if (loaded || typeof window === 'undefined' || !GA_MEASUREMENT_ID) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag
    || function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  // Clear any previous opt-out flag left by unloadGA4().
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;

  scriptEl = document.createElement('script');
  scriptEl.async = true;
  scriptEl.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  scriptEl.setAttribute('data-consent-injected', 'ga4');
  document.head.appendChild(scriptEl);

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID);

  loaded = true;
}

/**
 * Withdraws GA4 as far as is feasible client-side: sets the documented
 * `window['ga-disable-<id>']` opt-out flag (stops the loaded gtag runtime
 * from sending further hits), removes the injected `<script>` tag, and
 * clears the known GA cookies (`_ga`, `_ga_<container>`, `_gid`, `_gat`)
 * from both the current host and the registrable root domain.
 */
export function unloadGA4() {
  if (typeof window === 'undefined') return;

  window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;

  if (scriptEl?.parentNode) {
    scriptEl.parentNode.removeChild(scriptEl);
  }
  scriptEl = null;
  loaded = false;

  removeGaCookies();
}

const GA_COOKIE_PREFIXES = ['_ga', '_gid', '_gat'];

function removeGaCookies() {
  if (typeof document === 'undefined' || !document.cookie) return;

  const names = document.cookie
    .split(';')
    .map((pair) => pair.split('=')[0].trim())
    .filter(Boolean);

  const gaNames = names.filter((name) => GA_COOKIE_PREFIXES.some(
    (prefix) => name === prefix || name.startsWith(`${prefix}_`),
  ));

  const rootDomain = getRegistrableDomain();

  gaNames.forEach((name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    if (rootDomain) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${rootDomain};`;
    }
  });
}

/** Best-effort registrable-domain guess (last two labels), e.g. `.ateaseai.com`. */
function getRegistrableDomain() {
  const host = window.location?.hostname;
  if (!host || host === 'localhost') return null;
  const parts = host.split('.');
  if (parts.length <= 2) return null;
  return `.${parts.slice(-2).join('.')}`;
}
